// Touch screens get no click-to-zoom, even at desktop widths — the same as the
// live theme. Below this width the gallery is a carousel with pinch zoom.
const ZOOM_QUERY = "(min-width: 1024px) and (hover: hover) and (pointer: fine)";

/**
 * Desktop click-to-zoom for a gallery image. Clicking lays a larger copy over
 * the image; while zoomed, the point under the pointer stays under the pointer.
 */
class MediaZoom extends HTMLElement {
  connectedCallback() {
    this.query = window.matchMedia(ZOOM_QUERY);
    this.query.addEventListener("change", this._sync);
    this._sync();
  }

  disconnectedCallback() {
    this.query?.removeEventListener("change", this._sync);
    this._disable();
  }

  _sync = () => {
    if (this.query.matches) {
      this._enable();
    } else {
      this._disable();
    }
  };

  _enable() {
    if (this.enabled) return;

    this.enabled = true;
    this.setAttribute("role", "button");
    this.setAttribute("tabindex", "0");
    this.setAttribute("aria-label", this.dataset.label);
    this._setZoomed(false);

    this.addEventListener("pointerenter", this._createLayer);
    this.addEventListener("focus", this._createLayer);
    this.addEventListener("click", this._onClick);
    this.addEventListener("pointermove", this._onPointerMove);
    this.addEventListener("keydown", this._onKeydown);
  }

  _disable() {
    if (!this.enabled) return;

    this.enabled = false;
    this._setZoomed(false);
    ["role", "tabindex", "aria-label", "aria-pressed"].forEach((name) =>
      this.removeAttribute(name),
    );

    this.removeEventListener("pointerenter", this._createLayer);
    this.removeEventListener("focus", this._createLayer);
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("pointermove", this._onPointerMove);
    this.removeEventListener("keydown", this._onKeydown);
  }

  // Built on first hover or focus rather than on load, so the large images are
  // only fetched for media someone actually goes to zoom.
  _createLayer = () => {
    if (this.layer) return;

    this.layer = document.createElement("img");
    this.layer.className = "media-gallery__zoom-layer";
    this.layer.alt = "";
    this.layer.src = this.dataset.zoomSrc;
    this.layer.style.objectPosition = getComputedStyle(
      this.querySelector("img"),
    ).objectPosition;
    this.append(this.layer);
  };

  _onClick = (event) => {
    this._createLayer();
    this._panTo(event);
    this._setZoomed(!this.zoomed);
  };

  _onPointerMove = (event) => {
    if (this.zoomed) this._panTo(event);
  };

  _onKeydown = (event) => {
    if (event.key === "Escape" && this.zoomed) {
      this._setZoomed(false);
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    this._createLayer();
    this.style.removeProperty("--zoom-origin");
    this._setZoomed(!this.zoomed);
  };

  _panTo({ clientX, clientY }) {
    const { left, top, width, height } = this.getBoundingClientRect();
    const x = ((clientX - left) / width) * 100;
    const y = ((clientY - top) / height) * 100;

    this.style.setProperty("--zoom-origin", `${x}% ${y}%`);
  }

  _setZoomed(zoomed) {
    this.zoomed = zoomed;
    this.classList.toggle("is-zoomed", zoomed);

    if (this.enabled) {
      this.setAttribute("aria-pressed", String(zoomed));
    }
  }
}

if (!customElements.get("media-zoom")) {
  customElements.define("media-zoom", MediaZoom);
}
